'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Medal } from 'lucide-react'
import Skeleton from '@/components/elements/Skeleton'
import EmptyState from "../elements/EmptyState"
import { panelFocusRing } from "../elements/SidePanel"
import { useAuth } from '@/lib/commerce/hooks'
import { getLoyaltySummaryApi, getLoyaltyTransactionsApi, getLoyaltyVouchersApi } from '@/lib/commerce/services/loyalty-api'
import { navigateUrl } from '@/utils/client-request'
import { formatImageURL, formatNumber, formatPrice, formatDateTimeNormal } from '@/utils/util'
import { TRANSACTION_TYPE_LABELS, VOUCHER_STATUS_LABELS } from '../loyalty/labels'
import ConvertVoucherModal from '../loyalty/ConvertVoucherModal'

const PAGE_SIZE = 20
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const solidButton = `h-11 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-40 disabled:cursor-not-allowed ${panelFocusRing}`
const outlinedButton = `h-11 px-6 border border-foreground text-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background disabled:opacity-50 ${panelFocusRing}`
const sweepLink = `text-[13px] pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[0%_1px] hover:bg-size-[100%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${panelFocusRing}`

export default function ProfileLoyalty({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const { session, isLoggedIn } = useAuth()

  const [summary, setSummary] = useState<any>(null)
  const [vouchers, setVouchers] = useState<any[]>([])
  const [transactions, setTransactions] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const ctx = { vendorId: vendor?.id, session: session || undefined }

  useEffect(() => {
    if (!isLoggedIn) return
    let cancelled = false
    Promise.all([
      getLoyaltySummaryApi(ctx),
      getLoyaltyVouchersApi(ctx),
      getLoyaltyTransactionsApi(ctx, 1, PAGE_SIZE),
    ]).then(([summaryRes, vouchersRes, transactionsRes]) => {
      if (cancelled) return
      setSummary(summaryRes?.data || null)
      setVouchers(Array.isArray(vouchersRes?.data) ? vouchersRes.data : [])
      setTransactions(transactionsRes?.data?.transactions || [])
      setTotal(transactionsRes?.data?.total || 0)
    }).catch(() => {}).finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [isLoggedIn, refreshKey])

  const loadMore = async () => {
    setLoadingMore(true)
    const nextPage = page + 1
    const { data } = await getLoyaltyTransactionsApi(ctx, nextPage, PAGE_SIZE)
    setLoadingMore(false)
    if (!data?.transactions) return
    setTransactions((prev) => [...prev, ...data.transactions])
    setTotal(data.total || 0)
    setPage(nextPage)
  }

  const header = (
    <div className="pb-6 border-b border-border">
      <h1 className="text-2xl sm:text-3xl tracking-tight text-foreground">Лоялти оноо</h1>
      <p className="mt-2 text-sm text-muted-foreground">Таны цуглуулсан оноо болон онооны түүх</p>
    </div>
  )

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="py-8 border-b border-border grid grid-cols-2 gap-6">
          <Skeleton className="w-32 h-12 rounded-none" />
          <Skeleton className="w-40 h-12 rounded-none" />
        </div>
        <div className="divide-y divide-border">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between py-5">
              <div className="space-y-2">
                <Skeleton className="w-32 h-4 rounded-none" />
                <Skeleton className="w-48 h-3 rounded-none" />
              </div>
              <Skeleton className="w-14 h-4 rounded-none" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const balance = summary?.balance || 0
  const canConvert = balance >= (summary?.minPointsToConvert || 1)

  return (
    <div>
      {header}

      {summary && (
        <section className="py-8 border-b border-border flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8">
          <div className="flex flex-wrap items-end gap-x-12 gap-y-6">
            <div>
              <div className={eyebrow}>Таны түвшин</div>
              <div className="mt-3 flex items-center gap-3">
                {summary.currentTier?.icon ? (
                  <img src={formatImageURL(summary.currentTier.icon)} alt="" className="size-8 object-cover bg-muted" />
                ) : (
                  <div className="size-8 bg-muted grid place-items-center"><Medal aria-hidden className="size-4 text-muted-foreground" strokeWidth={1.25} /></div>
                )}
                <span className="text-lg text-foreground">{summary.currentTier?.name || '—'}</span>
              </div>
            </div>
            <div>
              <div className={eyebrow}>Онооны үлдэгдэл</div>
              <div className="mt-2 text-4xl font-light tracking-tight tabular-nums">{formatNumber(balance)}</div>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <a href={navigateUrl('/loyalty', pathname)} className={sweepLink}>Хөтөлбөр үзэх</a>
            <button type="button" onClick={() => setConvertOpen(true)} disabled={!canConvert} className={solidButton}>Ваучер болгох</button>
          </div>
        </section>
      )}

      {vouchers.length > 0 && (
        <section className="pt-10">
          <h2 className={eyebrow}>Миний ваучерууд</h2>
          <ul className="mt-2 divide-y divide-border border-b border-border">
            {vouchers.map((voucher: any) => {
              const active = voucher.status === 'active'
              return (
                <li key={voucher.id} className="flex items-center justify-between gap-4 py-5">
                  <div className="min-w-0">
                    <div className={`font-mono text-sm tracking-wide truncate ${active ? 'text-foreground' : 'text-muted-foreground'}`}>{voucher.code}</div>
                    <div className="mt-1 text-xs text-muted-foreground tabular-nums">{voucher.expireAt ? `${formatDateTimeNormal(voucher.expireAt)} хүртэл` : 'Хугацаагүй'}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className={`text-sm tabular-nums ${active ? 'text-foreground' : 'line-through text-muted-foreground'}`}>{formatPrice(Number(voucher.remainingAmount ?? voucher.amount) || 0)}</div>
                    <div className={`mt-1 text-[11px] tracking-[0.14em] uppercase ${active ? 'text-foreground' : 'text-muted-foreground'}`}>{VOUCHER_STATUS_LABELS[voucher.status] || voucher.status}</div>
                  </div>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      <section className="pt-10">
        {transactions.length === 0 ? (
          <EmptyState icon={Medal} title="Онооны түүх хоосон байна" description="Худалдан авалт хийж оноо цуглуулж эхлээрэй." />
        ) : (
          <>
            <h2 className={eyebrow}>Онооны түүх</h2>
            <ul className="mt-2 divide-y divide-border border-b border-border">
              {transactions.map((transaction: any) => (
                <li key={transaction.id} className="flex items-center justify-between gap-4 py-5">
                  <div className="min-w-0">
                    <div className="text-sm text-foreground">{TRANSACTION_TYPE_LABELS[transaction.type] || transaction.type}</div>
                    <div className="mt-1 text-xs text-muted-foreground truncate tabular-nums">{formatDateTimeNormal(transaction.createdAt)}{transaction.description ? ` · ${transaction.description}` : ''}</div>
                  </div>
                  <div className={`shrink-0 text-sm tabular-nums ${transaction.points < 0 ? 'text-muted-foreground' : 'text-foreground'}`}>
                    {transaction.points > 0 ? '+' : ''}{formatNumber(transaction.points)}
                  </div>
                </li>
              ))}
            </ul>
            {transactions.length < total && (
              <div className="mt-8 flex justify-center">
                <button type="button" onClick={loadMore} disabled={loadingMore} className={outlinedButton}>{loadingMore ? 'Ачаалж байна…' : 'Цааш үзэх'}</button>
              </div>
            )}
          </>
        )}
      </section>

      <ConvertVoucherModal open={convertOpen} setOpen={setConvertOpen} vendor={vendor} program={summary} balance={balance} onConverted={() => setRefreshKey((key) => key + 1)} />
    </div>
  )
}
